import { useMemo, useState } from "react"
import { MoonIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { t } from "@/lib/i18n"
import { patchInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"
import { WEEK, formatMinutes, weekdayName } from "@/features/schedule/schedule-time"

export type SchedulePeriod = {
  dayOfWeek: number
  startsAt: number
  endsAt: number
}

export type Schedule = {
  id: number | null
  name: string
  enabled: boolean
  timeZone: string
  periods: SchedulePeriod[]
}

export type TimezoneOption = {
  value: string
  label: string
}

type DayState = {
  active: boolean
  startsAt: number
  endsAt: number
}

/**
 * `timezones` is optional, and its absence hides the zone selector entirely.
 *
 * A `Schedule` carries its own `time_zone`, copied from its schedulable at build
 * time and never synced afterwards. On the business-hours screen that column IS
 * the restaurant's zone and editing it is the point. A promotion window has no
 * zone of its own — it borrows `Location#time_zone` — so exposing the field
 * there would let "martes de 3 a 6" drift into meaning two different hours
 * depending on which row the reader consults. The promotions controller does not
 * permit the parameter at all; this prop is what keeps the screen from offering
 * a control the server ignores.
 *
 * `testIdPrefix` exists because several of these can share a page: the
 * promotions screen renders one window editor per rule.
 */
type Props = {
  schedule: Schedule
  timezones?: TimezoneOption[]
  url?: string
  labels?: { enable: string; enableHint: string }
  testIdPrefix?: string
}

const DEFAULT_STARTS_AT = 9 * 60
const DEFAULT_ENDS_AT = 17 * 60 + 30
const TIME_STEP = 30
const TIME_OPTIONS = Array.from({ length: (24 * 60) / TIME_STEP }, (_, index) => index * TIME_STEP)

function buildWeek(schedule: Schedule): Record<number, DayState> {
  const week: Record<number, DayState> = {}
  WEEK.forEach((day) => {
    week[day] = { active: false, startsAt: DEFAULT_STARTS_AT, endsAt: DEFAULT_ENDS_AT }
  })

  // A never-saved schedule starts with a sensible Monday-Friday default
  if (schedule.id === null && schedule.periods.length === 0) {
    [1, 2, 3, 4, 5].forEach((day) => {
      week[day].active = true
    })
    return week
  }

  schedule.periods.forEach((period) => {
    week[period.dayOfWeek] = { active: true, startsAt: period.startsAt, endsAt: period.endsAt }
  })
  return week
}

function TimeSelect({ value, onChange, disabled, label, testId }: {
  value: number
  onChange: (value: number) => void
  disabled: boolean
  label: string
  testId?: string
}) {
  if (disabled) {
    return (
      <div className="text-muted-foreground flex h-10 flex-1 items-center justify-between rounded-md border border-dashed px-3 text-sm md:h-8">
        <MoonIcon className="size-3.5" />
        <span>{t("ui.react.components.features.schedule.business_hours_form.closed")}</span>
      </div>
    )
  }

  return (
    <div className="flex flex-1 items-center gap-2">
      <span className="text-muted-foreground text-xs">{label}</span>
      <Select value={String(value)} onValueChange={(next) => onChange(Number(next))}>
        <SelectTrigger className="flex-1" size="sm" data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TIME_OPTIONS.map((minutes) => (
            <SelectItem
              key={minutes}
              value={String(minutes)}
              data-testid={testId ? `${testId}-option-${minutes}` : undefined}
            >
              {formatMinutes(minutes)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export function BusinessHoursForm({
  schedule,
  timezones,
  url = "/manage/location/business_hours",
  labels,
  testIdPrefix = "business-hours",
}: Props) {
  const [submitting, setSubmitting] = useState(false)
  const [enabled, setEnabled] = useState(schedule.enabled)
  const [timeZone, setTimeZone] = useState(schedule.timeZone)
  const [week, setWeek] = useState<Record<number, DayState>>(() => buildWeek(schedule))

  const scope = "ui.react.components.features.schedule.business_hours_form"
  const dayNames = useMemo(() => Object.fromEntries(WEEK.map((day) => [day, weekdayName(day)])), [])

  const setDay = (day: number, patch: Partial<DayState>) => {
    setWeek((current) => ({ ...current, [day]: { ...current[day], ...patch } }))
  }

  const submit = () => {
    const periods = WEEK.filter((day) => week[day].active).map((day) => ({
      day_of_week: day,
      starts_at: week[day].startsAt,
      ends_at: week[day].endsAt,
    }))

    // The zone rides along only on the screen that offers the selector. The
    // promotions controller does not permit `time_zone` at all — sending one it
    // silently drops would make the payload claim a decision the screen never
    // let the operator make.
    const zone = timezones && timezones.length > 0 ? { time_zone: timeZone } : {}

    setSubmitting(true)
    patchInline(url, { schedule: { enabled, ...zone, periods } })
      .then((result) => toastInlineUpdateSuccess(result))
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => setSubmitting(false))
  }

  return (
    <div className="space-y-4">
      <div className="divide-y">
        <div className="flex items-center justify-between gap-4 py-3">
          <div>
            <p className="text-sm font-medium">{labels?.enable ?? t(`${scope}.enable`)}</p>
            <p className="text-muted-foreground text-xs">{labels?.enableHint ?? t(`${scope}.enable_hint`)}</p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={setEnabled}
            data-testid={`${testIdPrefix}-enabled`}
          />
        </div>

        {timezones && timezones.length > 0 && (
          <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div>
              <p className="text-sm font-medium">{t(`${scope}.timezone`)}</p>
              <p className="text-muted-foreground text-xs">{t(`${scope}.timezone_hint`)}</p>
            </div>
            <Select value={timeZone} onValueChange={setTimeZone}>
              <SelectTrigger className="w-full sm:max-w-56" size="sm" data-testid={`${testIdPrefix}-timezone`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timezones.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    data-testid={`${testIdPrefix}-timezone-option-${option.value}`}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2 py-3">
          {WEEK.map((day) => (
            <div key={day} className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <div className="flex w-full shrink-0 items-center gap-2 sm:w-32">
                <Switch
                  checked={week[day].active}
                  onCheckedChange={(active) => setDay(day, { active })}
                  data-testid={`${testIdPrefix}-day-${day}`}
                />
                <span className={week[day].active ? "text-sm capitalize" : "text-muted-foreground text-sm capitalize"}>
                  {dayNames[day]}
                </span>
              </div>
              <div className="flex w-full min-w-0 items-center gap-2 sm:flex-1">
                <TimeSelect
                  label={t(`${scope}.from`)}
                  value={week[day].startsAt}
                  disabled={!week[day].active}
                  onChange={(startsAt) => setDay(day, { startsAt })}
                  testId={`${testIdPrefix}-day-${day}-starts-at`}
                />
                <TimeSelect
                  label={t(`${scope}.to`)}
                  value={week[day].endsAt}
                  disabled={!week[day].active}
                  onChange={(endsAt) => setDay(day, { endsAt })}
                  testId={`${testIdPrefix}-day-${day}-ends-at`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={submit}
          disabled={submitting}
          data-testid={`${testIdPrefix}-save`}
        >
          {t(`${scope}.save`)}
        </Button>
      </div>
    </div>
  )
}
